import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { FUNCTIONALITIES, STAFF_BASE } from "./config";
import { useMenuEntitlement } from "../../hooks/useEntitlement";

// Single shell for the Gestión de Staff section. Derives the active
// functionality from the URL (/gestion-staff/<slug>/...), renders that
// functionality's sub-menu, and the matched section via <Outlet/>.
// Mirrors NuvexAccessLayout (Header + AppSidebarMenu + AppsSidebar + Outlet).
export default function StaffLayout() {
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Active functionality from URL: /gestion-staff/<slug>/<item>
  const parts = location.pathname.split("/").filter(Boolean); // ["gestion-staff", slug, item]
  const activeSlug = parts[1] ?? FUNCTIONALITIES[0].slug;
  const functionality =
    FUNCTIONALITIES.find((f) => f.slug === activeSlug) ?? FUNCTIONALITIES[0];
  // Cada funcionalidad es una app del catálogo (`gestion-staff-<slug>`); sus ítems,
  // `menu:gestion-staff-<slug>.<id>` (#803).
  const canSeeMenuItem = useMenuEntitlement(`gestion-staff-${functionality.slug}`);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = functionality.menu.filter((item) => canSeeMenuItem(item.id)).map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    path: `${STAFF_BASE}/${functionality.slug}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`flex h-screen pt-safe-top flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title="Gestión de Staff"
        subtitle={functionality.name}
        onOpenAppsSidebar={openAppsSidebar}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname === `${STAFF_BASE}/${functionality.slug}/${itemId}`
          }
          footerStats={{
            label: functionality.name,
            items: [
              { label: "Vistas", value: menuItems.length },
              { label: "Secciones", value: FUNCTIONALITIES.length, className: "text-primary-500" },
            ],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={`gestion-staff-${functionality.slug}`}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
